// Image caching utility to preload and cache images for smooth transitions
class ImageCache {
    private cache: Map<string, HTMLImageElement> = new Map();
    private loadingPromises: Map<string, Promise<HTMLImageElement>> = new Map();

    /**
     * Preload a single image and store it in cache
     */
    async preloadImage(src: string): Promise<HTMLImageElement> {
        // Return cached image if already loaded
        if (this.cache.has(src)) {
            return this.cache.get(src)!;
        }

        // Return existing loading promise if already in progress
        if (this.loadingPromises.has(src)) {
            return this.loadingPromises.get(src)!;
        }

        // Create new loading promise
        const loadingPromise = new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            
            img.onload = () => {
                this.cache.set(src, img);
                this.loadingPromises.delete(src);
                resolve(img);
            };
            
            img.onerror = () => {
                this.loadingPromises.delete(src);
                reject(new Error(`Failed to load image: ${src}`));
            };
            
            img.src = src;
        });

        this.loadingPromises.set(src, loadingPromise);
        return loadingPromise;
    }

    /**
     * Preload multiple images concurrently
     */
    async preloadImages(sources: string[]): Promise<HTMLImageElement[]> {
        const promises = sources.map(src => this.preloadImage(src));
        return Promise.all(promises);
    }

    /**
     * Check if an image is cached
     */
    isCached(src: string): boolean {
        return this.cache.has(src);
    }

    /**
     * Get cached image element (returns undefined if not cached)
     */
    getCachedImage(src: string): HTMLImageElement | undefined {
        return this.cache.get(src);
    }

    /**
     * Clear specific image from cache
     */
    clearImage(src: string): void {
        this.cache.delete(src);
        this.loadingPromises.delete(src);
    }

    /**
     * Clear entire cache
     */
    clearAll(): void {
        this.cache.clear();
        this.loadingPromises.clear();
    }

    /**
     * Get cache size (number of cached images)
     */
    getCacheSize(): number {
        return this.cache.size;
    }

    /**
     * Get all cached image sources
     */
    getCachedSources(): string[] {
        return Array.from(this.cache.keys());
    }
}

// Create singleton instance
export const imageCache = new ImageCache();

// Export the class for potential custom instances
export { ImageCache };

// Utility function for easy preloading
export const preloadImages = (sources: string[]): Promise<HTMLImageElement[]> => {
    return imageCache.preloadImages(sources);
};

// Utility function for single image preloading
export const preloadImage = (src: string): Promise<HTMLImageElement> => {
    return imageCache.preloadImage(src);
}; 